if (!customElements.get('bundle-builder')) {
  class BundleBuilder extends HTMLElement {
    connectedCallback() {
      this.slotsContainers = Array.from(this.querySelectorAll('[js-slots]'));
      this.slotsContainer = this.slotsContainers[0];
      this.cards = Array.from(this.querySelectorAll('[js-card]'));

      const firstSlot = this.slotsContainer && this.slotsContainer.querySelector('[js-slot]');
      this.emptySlotHTML = firstSlot ? firstSlot.outerHTML : '';

      const template = this.querySelector('template');
      this.filledSlotHTML = template ? template.innerHTML.trim() : '';

      this.bundleSize = parseInt(this.dataset.bundleSize, 10) || 0;

      this.soldOutPopup = this.querySelector('[js-soldout-popup]');
      this.bar = this.querySelector('[js-bar]');
      this.clearButtons = Array.from(this.querySelectorAll('[js-clear]'));
      this.atcButtons = Array.from(this.querySelectorAll('[js-atc]'));
      this.barNote = this.querySelector('[js-note]');
      this.noteTemplate = this.barNote ? this.barNote.dataset.template || '' : '';
      this.noteFull = this.barNote ? this.barNote.dataset.full || '' : '';
      this.variantAvailable = this.atcButtons.length ? !this.atcButtons[0].disabled : true;
      this.atcButtons.forEach((btn) => { btn.disabled = true; });

      const handle = this.dataset.productHandle || '';
      this.storageKey = handle ? 'bundle-selection:' + handle : '';

      this.userDirty = false;

      this.bindEvents();
      this.onKeydown = (event) => { if (event.key === 'Escape') this.hideSoldOut(); };
      document.addEventListener('keydown', this.onKeydown);
      this.bindReveal();
      this.bindPersistence();
      this.bindErrors();
      this.whenSelectsReady().then(() => {
        /* Start every slot empty. Simple Bundles' selects have no blank option and
           default to their first scent; disabling both marks the slot empty and stops
           that default from submitting into the cart. */
        this.selects.forEach((select) => { select.disabled = true; });
        this.restore();
        this.render();
        this.bindSelectsGuard();
      });
    }

    /* Simple Bundles re-renders its property selects after we disable them, re-enabling
       every one — so every slot reads as filled and + finds no empty slot to fill.
       Re-assert the empty baseline on each app re-render until the shopper's first pick. */
    bindSelectsGuard() {
      const form = this.querySelector('form');
      if (!form) return;
      this.selectsGuard = new MutationObserver(() => {
        if (this.userDirty) return;
        if (this.selects.some((select) => this.isActive(select))) {
          this.selects.forEach((select) => { select.disabled = true; });
          this.render();
        }
      });
      this.selectsGuard.observe(form, { childList: true, subtree: true });
    }

    /* Simple Bundles re-renders its injected property selects after load, detaching
       the original nodes. Caching them meant writing disabled/value to dead nodes
       while the live form kept its defaults; re-query every read so writes always land. */
    get selects() {
      const list = Array.from(document.querySelectorAll('form[action$="/cart/add"] select[name^="properties["]'));
      return this.bundleSize ? list.slice(0, this.bundleSize) : list;
    }

    readStored() {
      if (!this.storageKey) return [];
      try {
        const raw = window.localStorage.getItem(this.storageKey);
        const parsed = raw ? JSON.parse(raw) : [];
        return Array.isArray(parsed) ? parsed : [];
      } catch (e) {
        return [];
      }
    }

    writeStored() {
      if (!this.storageKey || !this.selects) return;
      try {
        const values = this.selects
          .filter((select) => this.isActive(select))
          .map((select) => select.value);
        window.localStorage.setItem(this.storageKey, JSON.stringify(values));
      } catch (e) {
        /* storage unavailable — ignore */
      }
    }

    clearStored() {
      if (!this.storageKey) return;
      try {
        window.localStorage.removeItem(this.storageKey);
      } catch (e) {
        /* storage unavailable — ignore */
      }
    }

    restore() {
      const stored = this.readStored();
      if (!stored.length) return;
      this.storedSelection = stored;
      this.applyStored(stored);
      const reapply = () => this.applyStored(this.storedSelection);
      if (document.readyState === 'complete') {
        setTimeout(reapply, 300);
      } else {
        window.addEventListener('load', () => setTimeout(reapply, 300), { once: true });
      }
    }

    applyStored(values) {
      if (!this.selects) return;
      if (values.length) this.userDirty = true;
      this.selects.forEach((select) => { select.disabled = true; });
      values.forEach((value) => {
        if (this.isSoldOut(value)) return;
        const next = this.selects.find((select) => !this.isActive(select));
        if (next) this.activate(next, value);
      });
      this.render();
    }

    bindPersistence() {
      const form = this.querySelector('form');
      if (form) form.addEventListener('submit', () => this.clearStored());
    }

    /* product-form (theme.js) fires cart:error on a failed add — e.g. a sold-out
       scent Shopify only rejects at submit. Show it; otherwise the add silently no-ops. */
    bindErrors() {
      const form = this.querySelector('form');
      if (!form) return;
      form.addEventListener('cart:error', (event) => {
        const message = event.detail && event.detail.error;
        if (!message) return;
        const banner = document.createRange().createContextualFragment(
          `<div class="banner banner--error" role="alert">${message}</div>`
        ).firstElementChild;
        form.prepend(banner);
        setTimeout(() => { banner.remove(); }, 2500);
      });
    }

    whenSelectsReady() {
      const query = () => {
        const list = Array.from(document.querySelectorAll('form[action$="/cart/add"] select[name^="properties["]'));
        if (!list.length) return null;
        if (!list.every((select) => select.options.length > 1)) return null;
        return list;
      };

      return new Promise((resolve) => {
        const existing = query();
        if (existing) return resolve(existing);

        const observer = new MutationObserver(() => {
          const found = query();
          if (found) {
            observer.disconnect();
            resolve(found);
          }
        });

        observer.observe(document.body, { childList: true, subtree: true });
      });
    }

    bindReveal() {
      if (!this.bar) return;
      const anchor = this.querySelector('form [js-atc]');
      if (!anchor) return;
      this.revealObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          this.bar.classList.toggle('is-visible', !entry.isIntersecting);
        });
      });
      this.revealObserver.observe(anchor);
    }

    disconnectedCallback() {
      if (this.revealObserver) this.revealObserver.disconnect();
      if (this.selectsGuard) this.selectsGuard.disconnect();
      if (this.onKeydown) document.removeEventListener('keydown', this.onKeydown);
    }

    bindEvents() {
      this.addEventListener('click', (event) => {
        const isMobile = window.matchMedia('(max-width: 699px)').matches;

        // Any click closes tooltips on other cards.
        if (isMobile) {
          const clickedCard = event.target.closest('[js-card]');
          this.cards.forEach((c) => { if (c !== clickedCard) c.classList.remove('is-revealed'); });
        }

        // Controls act without toggling the tooltip, so handle them first.
        const control = event.target.closest('[data-action]');
        if (control && this.contains(control)) {
          const card = control.closest('[js-card]');
          if (!card) return;
          const value = card.dataset.title;
          if (control.dataset.action === 'increase') this.increase(value);
          else this.decrease(value);
          return;
        }

        const soldOutClose = event.target.closest('[js-soldout-close], [js-soldout-overlay]');
        if (soldOutClose && this.contains(soldOutClose)) {
          this.hideSoldOut();
          return;
        }

        const remove = event.target.closest('[js-slot-remove]');
        if (remove) {
          const slot = remove.closest('[js-slot]');
          if (slot) this.clearSlot(parseInt(slot.dataset.index, 10));
          return;
        }

        const clear = event.target.closest('[js-clear]');
        if (clear && this.contains(clear)) {
          event.preventDefault();
          this.clearAll();
          return;
        }

        const choose = event.target.closest('[js-choose]');
        if (choose && this.contains(choose)) {
          event.preventDefault();
          this.chooseSelection();
          return;
        }

        // Anywhere else on the card toggles that card's tooltip (mobile only).
        if (isMobile) {
          const card = event.target.closest('[js-card]');
          if (card && this.contains(card)) {
            event.preventDefault();
            card.classList.toggle('is-revealed');
          }
        }
      });
    }

    normalizeName(str) {
      return String(str).replace(/ travel candle/i, '').trim().toLowerCase();
    }

    parseBestsellers(raw) {
      return raw.split(',').map((part) => {
        const match = part.match(/^(.*?)[\s-]+(\d+)\s*$/);
        if (match) return { name: match[1].trim(), count: parseInt(match[2], 10) || 1 };
        const name = part.trim();
        return name ? { name: name, count: 1 } : null;
      }).filter(Boolean);
    }

    chooseSelection() {
      if (!this.selects || !this.selects.length) return;
      this.userDirty = true;

      this.selects.forEach((select) => { select.disabled = true; });

      const hasEmpty = () => this.selects.some((select) => !this.isActive(select));
      const place = (value) => {
        const next = this.selects.find((select) => !this.isActive(select));
        return next ? this.activate(next, value) : false;
      };

      const raw = (this.dataset.bestsellers || '').trim();
      if (raw) {
        const titleByName = new Map();
        this.cards.forEach((card) => {
          if (card.dataset.title) titleByName.set(this.normalizeName(card.dataset.title), card.dataset.title);
        });

        for (const entry of this.parseBestsellers(raw)) {
          const value = titleByName.get(this.normalizeName(entry.name));
          if (!value || this.isSoldOut(value)) continue;
          for (let i = 0; i < entry.count && hasEmpty(); i++) place(value);
          if (!hasEmpty()) break;
        }
      }

      const values = this.cards
        .filter((card) => !card.dataset.soldout)
        .map((card) => card.dataset.title)
        .filter(Boolean);
      let guard = 0;
      while (values.length && hasEmpty() && guard < 500) {
        guard++;
        place(values[Math.floor(Math.random() * values.length)]);
      }

      this.render();
    }

    activate(select, value) {
      const option = Array.from(select.options).find((o) => o.value === value);
      if (!option) return false;
      select.disabled = false;
      select.value = value;
      select.dispatchEvent(new Event('change', { bubbles: true }));
      return true;
    }

    deactivate(select) {
      select.disabled = true;
      select.dispatchEvent(new Event('change', { bubbles: true }));
    }

    isActive(select) {
      return !select.disabled;
    }

    cardFor(value) {
      return this.cards.find((card) => card.dataset.title === value);
    }

    isSoldOut(value) {
      const card = this.cardFor(value);
      return !!(card && card.dataset.soldout);
    }

    showSoldOut() {
      if (!this.soldOutPopup) return;
      this.soldOutPopup.hidden = false;
      const close = this.soldOutPopup.querySelector('[js-soldout-close]');
      if (close) close.focus();
    }

    hideSoldOut() {
      if (this.soldOutPopup) this.soldOutPopup.hidden = true;
    }

    increase(value) {
      if (!this.selects) return;
      if (this.isSoldOut(value)) { this.showSoldOut(); return; }
      this.userDirty = true;
      const next = this.selects.find((select) => !this.isActive(select));
      if (!next) return;
      if (this.activate(next, value)) this.render();
    }

    decrease(value) {
      if (!this.selects) return;
      this.userDirty = true;
      const held = this.selects.filter((select) => this.isActive(select) && select.value === value);
      const last = held[held.length - 1];
      if (!last) return;
      this.deactivate(last);
      this.render();
    }

    clearAll() {
      if (!this.selects) return;
      this.userDirty = true;
      this.selects.forEach((select) => { if (this.isActive(select)) this.deactivate(select); });
      this.clearStored();
      this.render();
    }

    clearSlot(index) {
      if (!this.selects || Number.isNaN(index)) return;
      this.userDirty = true;
      const select = this.selects[index];
      if (!select) return;
      this.deactivate(select);
      this.render();
    }

    label(value) {
      return value ? value.replace(' Travel Candle', '') : '';
    }

    imageFor(value) {
      const card = this.cards.find((c) => c.dataset.title === value);
      const img = card && card.querySelector('img');
      if (!img) return '';
      return img.currentSrc || img.src || '';
    }

    renderSlots() {
      if (!this.slotsContainers.length) return;

      const html = this.selects.map((select, index) => {
        const active = this.isActive(select);
        const wrapper = document.createElement('div');
        wrapper.innerHTML = active ? this.filledSlotHTML : this.emptySlotHTML;
        const slot = wrapper.firstElementChild;
        slot.dataset.index = index;

        if (active) {
          const image = slot.querySelector('img');
          if (image) {
            image.src = this.imageFor(select.value);
            image.alt = this.label(select.value);
          }
          const title = slot.querySelector('[js-slot-title]');
          if (title) title.textContent = this.label(select.value);
        }

        return slot.outerHTML;
      }).join('');

      this.slotsContainers.forEach((container) => { container.innerHTML = html; });
    }

    renderCards() {
      this.cards.forEach((card) => {
        const value = card.dataset.title;
        const count = this.selects.filter((select) => this.isActive(select) && select.value === value).length;

        card.classList.toggle('is-active', count > 0);

        const badge = card.querySelector('[js-badge]');
        if (!badge) return;
        if (count >= 1) {
          badge.hidden = false;
          badge.textContent = count;
        } else {
          badge.hidden = true;
          badge.textContent = '';
        }
      });
    }

    renderControls() {
      const remaining = this.selects.filter((select) => !this.isActive(select)).length;
      const full = remaining === 0;
      this.querySelectorAll('[data-action="increase"]').forEach((btn) => {
        btn.disabled = full;
      });
      const atcDisabled = !full || !this.variantAvailable;
      this.atcButtons.forEach((btn) => { btn.disabled = atcDisabled; });
      const anyActive = this.selects.some((select) => this.isActive(select));
      this.clearButtons.forEach((btn) => { btn.hidden = !anyActive; });
      if (this.barNote) {
        this.barNote.hidden = false;
        this.barNote.textContent = full ? this.noteFull : this.noteTemplate.replace('__count__', remaining);
      }
    }

    render() {
      this.renderSlots();
      this.renderCards();
      this.renderControls();
      this.writeStored();
    }
  }

  customElements.define('bundle-builder', BundleBuilder);
}
